import { useState } from "react";
import { Table, Button, Modal, Form, Select, InputNumber, Popconfirm, message } from "antd";
import { Plus } from "lucide-react";
import { useHRUsers, useHoliday, useHolidayUpsert, useHolidayDelete } from "../../../../api-hooks/hr";

const TAX_YEARS = ["2025-26","2024-25","2023-24","2022-23"];

export default function HolidayTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [taxYear, setTaxYear] = useState("2025-26");

  const { data: users = [] } = useHRUsers(cid);
  const { data: rows = [], isLoading } = useHoliday(cid, { tax_year: taxYear });

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const upsert = useHolidayUpsert(cid, cb("Saved.", () => setModal({ open: false, rec: null })));
  const del = useHolidayDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Employee", dataIndex: "user_name", key: "u" },
    { title: "Tax Year", dataIndex: "tax_year", key: "ty" },
    { title: "Total Days", dataIndex: "total_days", key: "td" },
    { title: "Carried Fwd", dataIndex: "carried_forward", key: "cf" },
    { title: "Taken", dataIndex: "taken_days", key: "tk" },
    { title: "Remaining", dataIndex: "remaining_days", key: "r", render: (v: number) => <span className={v < 5 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>{v}</span> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <div className="flex gap-1">
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue(r); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </div>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <Select value={taxYear} onChange={setTaxYear} options={TAX_YEARS.map(v => ({ value: v, label: v }))} style={{ width: 140 }} />
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ tax_year: taxYear, total_days: 28, carried_forward: 0, taken_days: 0 }); }} style={{ background: "#4c1d95" }}>Add / Update</Button>
      </div>
      <Table rowKey="id" dataSource={rows as any[]} columns={cols} loading={isLoading} size="small" pagination={false} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title="Holiday Entitlement" width={420}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => upsert.mutate(vals)}>
          <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}><Select options={(users as any[]).map(u => ({ value: u.id, label: u.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="tax_year" label="Tax Year" rules={[{ required: true }]}><Select options={TAX_YEARS.map(v => ({ value: v, label: v }))} /></Form.Item>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="total_days" label="Total Days"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
            <Form.Item name="carried_forward" label="Carried Fwd"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
            <Form.Item name="taken_days" label="Taken"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={upsert.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
